<template>
  <div>
    <pack-info :packId="packId"></pack-info>
    <v-container fluid>
      <v-row class="och-topMargin--packInfoOffset" no-gutters>
        <v-col sm="12">
          <v-toolbar dense dark color="primary white--text elevation-0">
            <v-toolbar-title class="font-weight-bold">
              {{ releaseNote.title }}
              <span class="font-weight-thin">(ID: {{ releaseNoteId }})</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-title class="font-weight-thin"
              >Documentation Status: {{ releaseNote.documentationStatus }}
              <span
                v-if="releaseNote.documentationStatus === 'Published'"
                data-cy="releaseNoteView__text--datePublished"
              >
                {{ releaseNote.datePublished }}
              </span></v-toolbar-title
            >
            <v-btn icon @click="edit" v-if="permissions.releaseNoteCreateEdit" data-cy="releaseNote__button--edit">
              <v-icon small>fas fa-edit</v-icon>
            </v-btn>
            <release-note-delete
              :releaseNote="releaseNote"
              :packId="packId"
              data-cy="deleteButton"
              v-if="permissions.releaseNoteDelete"
            ></release-note-delete>
          </v-toolbar>
          <v-sheet
            v-if="releaseNote.isGovernedTemplate"
            class="py-1 px-4 d-flex align-center"
            color="blue-grey darken-2"
            data-cy="releaseNoteView__banner--isGovernedTemplate"
          >
            <v-icon color="white" x-small class="mr-1">fas fa-clone</v-icon>
            <span class="white--text font-weight-medium caption">Governed Template</span>
            <v-spacer></v-spacer>
            <release-note-direct-copy
              :releaseNote="releaseNote"
              v-if="showDirectCopy"
            ></release-note-direct-copy>
          </v-sheet>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col sm="12" class="och-border--all">
          <v-tabs v-model="activeTab" :background-color="`${theme.primaryColor} lighten-5`">
            <v-tab ripple>Release Note</v-tab>
            <v-tab ripple v-if="showTestPlanTab" data-cy="releaseNoteView__tab--testPlan">Test Plan</v-tab>
            <v-tab ripple data-cy="releaseNoteView__tab--changeHistory">Change History</v-tab>
            <v-tab-item>
              <release-note :packId="packId" :releaseNoteId="releaseNoteId"></release-note>
            </v-tab-item>
            <v-tab-item v-if="showTestPlanTab">
              <test-step-table :packId="packId" :releaseNoteId="releaseNoteId"></test-step-table>
            </v-tab-item>
            <v-tab-item>
              <div
                class="caption d-flex align-center px-4 pt-3"
                data-cy="releaseNoteView__text--sourceTemplate"
                v-if="showSourceTemplateLink"
              >
                <v-icon x-small class="mr-2">fas fa-clone</v-icon>
                <span class="mr-1">Created from template:</span>
                <router-link
                  :to="{
                    name: 'releaseNoteView',
                    params: { packId: releaseNote.sourceTemplatePackId, releaseNoteId: releaseNote.sourceTemplateId },
                  }"
                  data-cy="releaseNoteView__link--sourceTemplate"
                >
                  {{ releaseNote.sourceTemplateTitle }} (ID: {{ releaseNote.sourceTemplateId }})
                </router-link>
              </div>
              <release-note-audit-log :releaseNoteId="releaseNoteId"></release-note-audit-log>
            </v-tab-item>
            <v-spacer></v-spacer>
          </v-tabs>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapState } from "vuex";

import PackInfo from "@/components/packInfo";
import ReleaseNote from "@/components/releaseNote";
import TestStepTable from "@/components/testStepTable";

import ReleaseNoteAuditLog from "./edit/ReleaseNoteAuditLog";
import ReleaseNoteDelete from "./ReleaseNoteDelete.vue";
import ReleaseNoteDirectCopy from "./ReleaseNoteDirectCopy.vue";

export default {
  name: "releaseNoteView",
  components: {
    PackInfo,
    ReleaseNote,
    ReleaseNoteAuditLog,
    ReleaseNoteDelete,
    ReleaseNoteDirectCopy,
    TestStepTable,
  },
  computed: {
    activeTab: {
      get() {
        const { tab } = this.$route.query;
        const index = this.tabs.indexOf(tab);
        return index < 0 ? 0 : index;
      },
      set(index) {
        const tab = this.tabs[index];
        if (!tab) {
          // The tabs are only known once the release note has loaded; pushing an undefined tab duplicates the route.
          return;
        }
        this.$router.push({ query: { tab } });
      },
    },
    permissions() {
      return this.$store.state.session.user.permissions;
    },
    releaseNote() {
      return this.$store.state.releaseNote.releaseNoteRecord;
    },
    releaseNoteId() {
      return parseInt(this.$route.params.releaseNoteId, 10);
    },
    showDirectCopy() {
      return !!this.releaseNote.isGovernedTemplate && !!this.permissions.releaseNoteCreateEdit && this.isOchinUser;
    },
    showSourceTemplateLink() {
      return !!this.releaseNote.sourceTemplateId && this.isOchinUser;
    },
    showTestPlanTab() {
      let releaseNoteDatePublished;
      if (this.releaseNote.datePublished) {
        releaseNoteDatePublished = new Date(this.releaseNote.datePublished).getTime();
      }
      const dateConditionForShowingTestPlanTab = new Date("12/16/2024").getTime();

      if (releaseNoteDatePublished && releaseNoteDatePublished < dateConditionForShowingTestPlanTab) {
        // Before 12/16/2024, hide the test plan tab in all cases excepct when the operationalize type is "UAT Needed"
        return this.operationalizeType === "UAT Needed";
      }

      // On or after 12/16/2024, show the test plan tab in all cases except when the operationalize type is "FYI" or the template type is "rte" or "communicationOnly"
      if (this.templateType === "rte" || this.templateType === "communicationOnly") {
        return false;
      }
      if (this.operationalizeType !== "FYI") {
        return true;
      }
      return false;
    },
    testStepsIsActiveTab() {
      return this.$route.query.tab === "test-steps";
    },
    theme() {
      return this.$store.state.session.theme;
    },
    ...mapState({
      isOchinUser: state => state.session.user.isOchinUser,
      operationalizeType: state => state.releaseNote.releaseNoteRecord.operationalizeType,
      templateType: state => state.releaseNote.releaseNoteRecord.templateType,
    }),
  },
  async created() {
    await this.getReleaseNote(this.releaseNoteId);
  },
  watch: {
    // Navigating from one release note view to another (e.g. the "Created from template" link) reuses this component.
    "$route.params.releaseNoteId"() {
      this.packId = parseInt(this.$route.params.packId, 10);
      return this.getReleaseNote(this.releaseNoteId);
    },
  },
  data() {
    return {
      packId: parseInt(this.$route.params.packId, 10),
      tabs: [],
    };
  },
  methods: {
    edit() {
      this.$router.push({
        name: "releaseNoteEdit",
        params: {
          packId: this.packId,
          releaseNoteId: this.releaseNoteId,
        },
      });
    },
    async getReleaseNote(id) {
      try {
        await this.$store.dispatch("releaseNote/getReleaseNote", {
          id,
        });
      } catch (e) {
        console.error(e);
        this.$notifyError("Could not load the release note.");
        return;
      }
      this.setRelevantTabs();
    },
    setRelevantTabs() {
      if (this.showTestPlanTab) {
        this.tabs = ["release-note", "test-steps", "change-history"];
      } else {
        this.tabs = ["release-note", "change-history"];
      }
    },
  },
};
</script>
